import { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { NotificationBell } from '../../components/NotificationBell';
import { LanguageSelector } from '../../components/LanguageSelector';
import {
  User,
  Home,
  Calendar,
  DollarSign,
  Award,
  Users,
  TrendingUp,
  BookOpen,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Star,
  Briefcase,
  Bell,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Employee Auth Context
const EmployeeAuthContext = createContext(null);

export const useEmployeeAuth = () => {
  const context = useContext(EmployeeAuthContext);
  if (!context) {
    throw new Error('useEmployeeAuth must be used within EmployeeAuthProvider');
  }
  return context;
};

export const EmployeePortalLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const token = localStorage.getItem('employee_token');

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchEmployeeProfile();
  }, [token]);

  const fetchEmployeeProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employee-portal/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployee(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      localStorage.removeItem('employee_token');
      localStorage.removeItem('employee_info');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('employee_token');
    localStorage.removeItem('employee_info');
    navigate('/employee/login');
  };

  const api = {
    get: (url) => axios.get(`${API_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } }),
    post: (url, data) => axios.post(`${API_URL}${url}`, data, { headers: { Authorization: `Bearer ${token}` } }),
    patch: (url, data) => axios.patch(`${API_URL}${url}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token || !employee) {
    return <Navigate to="/employee/login" replace />;
  }

  const navigation = [
    { name: 'Dashboard', href: '/employee', icon: Home },
    { name: 'My Schedule', href: '/employee/schedule', icon: Calendar },
    { name: 'My Pay', href: '/employee/pay', icon: DollarSign },
    { name: 'Performance', href: '/employee/performance', icon: TrendingUp },
    { name: 'Training', href: '/employee/training', icon: BookOpen },
    { name: 'Messages', href: '/employee/messages', icon: MessageSquare },
    { name: 'My Profile', href: '/employee/profile', icon: User },
  ];

  return (
    <EmployeeAuthContext.Provider value={{ employee, api, logout: handleLogout, refreshProfile: fetchEmployeeProfile }}>
      <div className="min-h-screen bg-[#f5f5f5]">
        {/* Mobile Header */}
        <header className="bg-[#1a1a1a] text-white sticky top-0 z-50 safe-area-top">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              {employee.profile_picture_url ? (
                <img 
                  src={employee.profile_picture_url} 
                  alt={employee.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#C6A87C] flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">{employee.name}</p>
                <p className="text-[#C6A87C] text-xs">{employee.role}</p>
              </div>
            </div>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {menuOpen && (
              <motion.nav
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10 overflow-hidden"
              >
                <div className="px-2 py-3 space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-[#C6A87C] text-white' 
                            : 'text-gray-300 hover:bg-white/5'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.name}</span>
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      </Link>
                    );
                  })}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-lg w-full transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Sign Out</span>
                  </button>
                </div>
              </motion.nav>
            )}
          </AnimatePresence>
        </header>

        {/* Main Content */}
        <main className="pb-20">
          <Outlet />
        </main>

        {/* Bottom Navigation (Mobile) */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom z-50">
          <div className="flex justify-around items-center py-2">
            {navigation.slice(0, 5).map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                    isActive ? 'text-[#C6A87C]' : 'text-gray-500'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{item.name.split(' ')[0]}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </EmployeeAuthContext.Provider>
  );
};

export default EmployeePortalLayout;
